import { create } from 'zustand'
import type { UsageData, SessionCost, ModelCategory } from '@/types/provider'

interface CostState {
  // Usage history
  sessionUsage: UsageData[]

  // Totals
  totals: SessionCost

  // Actions
  recordUsage: (usage: UsageData) => void
  setSessionCost: (cost: SessionCost) => void
  clearSession: () => void
  getFormattedTotal: () => string
}

const initialTotals: SessionCost = {
  analysis: {
    apiCalls: 0,
    tokens: { input: 0, output: 0, total: 0 },
    imageCount: 0,
    cost: { usd: 0, jpy: 0 },
  },
  generation: {
    apiCalls: 0,
    tokens: { input: 0, output: 0, total: 0 },
    imageCount: 0,
    cost: { usd: 0, jpy: 0 },
  },
  text: {
    apiCalls: 0,
    tokens: { input: 0, output: 0, total: 0 },
    imageCount: 0,
    cost: { usd: 0, jpy: 0 },
  },
  tts: {
    apiCalls: 0,
    tokens: { input: 0, output: 0, total: 0 },
    imageCount: 0,
    cost: { usd: 0, jpy: 0 },
  },
  video: {
    apiCalls: 0,
    tokens: { input: 0, output: 0, total: 0 },
    imageCount: 0,
    cost: { usd: 0, jpy: 0 },
  },
  total: { usd: 0, jpy: 0 },
}

export const useCostStore = create<CostState>((set, get) => ({
  sessionUsage: [],
  totals: initialTotals,

  recordUsage: (usage) => set((state) => {
    const newSessionUsage = [...state.sessionUsage, usage]

    // Deep clone initialTotals to avoid mutation
    const newTotals: SessionCost = {
      analysis: {
        apiCalls: 0,
        tokens: { input: 0, output: 0, total: 0 },
        imageCount: 0,
        cost: { usd: 0, jpy: 0 },
      },
      generation: {
        apiCalls: 0,
        tokens: { input: 0, output: 0, total: 0 },
        imageCount: 0,
        cost: { usd: 0, jpy: 0 },
      },
      text: {
        apiCalls: 0,
        tokens: { input: 0, output: 0, total: 0 },
        imageCount: 0,
        cost: { usd: 0, jpy: 0 },
      },
      tts: {
        apiCalls: 0,
        tokens: { input: 0, output: 0, total: 0 },
        imageCount: 0,
        cost: { usd: 0, jpy: 0 },
      },
      video: {
        apiCalls: 0,
        tokens: { input: 0, output: 0, total: 0 },
        imageCount: 0,
        cost: { usd: 0, jpy: 0 },
      },
      total: { usd: 0, jpy: 0 },
    }

    for (const u of newSessionUsage) {
      // Validate category before accessing
      const validCategories: ModelCategory[] = ['analysis', 'generation', 'text', 'tts', 'video']
      if (!validCategories.includes(u.category)) {
        console.warn(`[CostStore] Unknown usage category: ${u.category}`)
        continue
      }

      const cat = newTotals[u.category]
      cat.apiCalls++
      cat.tokens.input += u.tokens.input
      cat.tokens.output += u.tokens.output
      cat.tokens.total += u.tokens.total
      cat.imageCount += u.imageCount || 0
      cat.cost.usd += u.cost.usd
      cat.cost.jpy += u.cost.jpy
    }

    newTotals.total.usd =
      newTotals.analysis.cost.usd +
      newTotals.generation.cost.usd +
      newTotals.text.cost.usd +
      newTotals.tts.cost.usd +
      newTotals.video.cost.usd

    newTotals.total.jpy =
      newTotals.analysis.cost.jpy +
      newTotals.generation.cost.jpy +
      newTotals.text.cost.jpy +
      newTotals.tts.cost.jpy +
      newTotals.video.cost.jpy

    return {
      sessionUsage: newSessionUsage,
      totals: newTotals,
    }
  }),

  setSessionCost: (cost) => set({ totals: cost }),

  clearSession: () => set({
    sessionUsage: [],
    totals: initialTotals,
  }),

  getFormattedTotal: () => {
    const { totals } = get()
    return `¥${totals.total.jpy.toFixed(2)} ($${totals.total.usd.toFixed(4)})`
  },
}))
